import type { ServerVerification } from '@sd/contracts/ipc';
import { Icon, type IconName } from '../../icons/Icon';
import { ServerBadge } from '../../ui/Avatar';
import { Button, Notice } from '../../ui/controls';
import { formatFullDate, shortInstanceId } from '../shared/time';

type FailReason = Extract<ServerVerification, { ok: false }>['reason'];

const FAILURES: Record<
  FailReason,
  { title: string; check: string; code?: string; tone: 'danger' | 'warn'; icon: IconName; text: string; retry?: boolean }
> = {
  invalid_address: {
    title: '주소 형식이 올바르지 않아요',
    check: '서버 주소를 해석할 수 없어요',
    tone: 'warn',
    icon: 'info',
    text: '관리자에게 받은 주소를 다시 확인하세요. 예: messenger.company.example',
  },
  insecure_http: {
    title: '서버를 확인할 수 없어요',
    check: 'HTTPS가 아닌 주소예요',
    code: 'http://',
    tone: 'danger',
    icon: 'shield',
    text: '보안 연결(HTTPS)을 쓰는 회사 서버에만 연결해요. 주소가 https:// 로 시작하는지 회사 관리자에게 확인하세요.',
  },
  tls_error: {
    title: '서버를 확인할 수 없어요',
    check: 'HTTPS 인증서를 신뢰할 수 없어요',
    code: '발급자 불명',
    tone: 'danger',
    icon: 'shield',
    text: '안전하지 않은 연결이라 연결하지 않았어요. 주소를 다시 확인하거나 회사 관리자에게 문의하세요. 사설 인증서를 쓰는 회사라면 관리자가 PC에 인증서를 배포해야 해요.',
  },
  unreachable: {
    title: '서버에 연결할 수 없어요',
    check: '서버가 응답하지 않아요',
    tone: 'warn',
    icon: 'wifioff',
    text: '주소가 맞는지, 인터넷에 연결돼 있는지 확인한 뒤 다시 시도하세요.',
    retry: true,
  },
  not_sd_messenger: {
    title: 'sd-messenger 서버가 아니에요',
    check: '회사 메신저 서버 정보를 찾지 못했어요',
    code: '/.well-known/sd-messenger',
    tone: 'danger',
    icon: 'shield',
    text: '이 주소는 sd-messenger 회사 서버가 아니에요. 관리자에게 받은 주소를 다시 확인하세요.',
  },
  incompatible: {
    title: '버전이 호환되지 않아요',
    check: '이 앱 버전을 지원하지 않는 서버예요',
    tone: 'warn',
    icon: 'alert',
    text: '앱을 업데이트하거나 회사 관리자에게 서버 업데이트를 요청하세요.',
  },
  identity_changed: {
    title: '서버 신원이 바뀌었어요',
    check: '등록된 인스턴스 ID와 달라요',
    tone: 'danger',
    icon: 'shield',
    text: '이전과 다른 서버가 응답했어요. 서버를 다시 설치했거나 다른 VM으로 옮겼을 수도 있고, 연결이 가로채졌을 수도 있어요. 회사 관리자에게 확인한 뒤 이전 서버를 삭제하고 다시 추가하세요.',
  },
};

/** Which side is too old, when the app could tell (protocol versions differ). */
const INCOMPATIBLE = {
  UNSUPPORTED_CLIENT: { check: '이 앱보다 새 버전의 서버예요', text: '앱을 업데이트하면 이 회사 서버에 연결할 수 있어요.' },
  UNSUPPORTED_SERVER: { check: '이 앱보다 오래된 서버예요', text: '회사 관리자에게 서버 업데이트를 요청하세요. 업데이트가 끝나면 다시 연결할 수 있어요.' },
} as const;

function displayUrl(address: string): string {
  const value = address.trim();
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

function Check({ ok, label, code }: { ok: boolean; label: string; code?: string | null }) {
  return (
    <div className={`vf-c${ok ? '' : ' bad'}`}>
      <span className="ck">
        <Icon name={ok ? 'check' : 'x'} size="xs" />
      </span>
      {label}
      {code ? <code>{code}</code> : null}
    </div>
  );
}

/** The `.verify` box of step 2: what was checked before anything is registered. */
export function VerifyResult({
  address,
  verification,
  appVersion,
  onRetry,
}: {
  address: string;
  verification: ServerVerification | null;
  appVersion: string | null;
  onRetry: () => void;
}) {
  if (!verification) {
    return (
      <div className="verify">
        <div className="vf-co">
          <span className="srv" style={{ background: 'var(--blue-soft)', color: 'var(--blue-ink)', boxShadow: 'none' }}>
            <Icon name="sync" className="spin" />
          </span>
          <div>
            <b>서버를 확인하는 중…</b>
            <span>{displayUrl(address)}</span>
          </div>
        </div>
      </div>
    );
  }

  if (!verification.ok) {
    const failure = verification.reason === 'incompatible' && verification.code ? { ...FAILURES.incompatible, ...INCOMPATIBLE[verification.code] } : FAILURES[verification.reason];
    return (
      <div className="verify" role="alert">
        <div className="vf-co">
          <span className="srv" style={{ background: `var(--${failure.tone}-soft)`, color: `var(--${failure.tone})`, boxShadow: 'none' }}>
            <Icon name="alert" />
          </span>
          <div>
            <b>{failure.title}</b>
            <span>{displayUrl(address)}</span>
          </div>
        </div>
        <div className="vf-checks">
          <Check ok={false} label={failure.check} code={verification.detail ?? failure.code ?? null} />
        </div>
        <Notice tone={failure.tone} icon={failure.icon}>
          {failure.text}
        </Notice>
        {failure.retry ? (
          <div>
            <Button size="sm" icon="retry" onClick={onRetry}>
              다시 확인
            </Button>
          </div>
        ) : null}
      </div>
    );
  }

  const { tls, compatible } = verification;
  return (
    <div className="verify">
      <div className="vf-co">
        <ServerBadge serverId={verification.instanceId} companyName={verification.companyName} />
        <div>
          <b>{verification.companyName}</b>
          <span>{verification.serverUrl}</span>
        </div>
      </div>
      <div className="vf-checks">
        <Check
          ok={tls.secure}
          label={tls.secure ? 'HTTPS 인증서 확인' : 'HTTPS가 아닌 개발용 연결'}
          code={tls.secure ? (tls.validTo ? `유효 · ${formatFullDate(tls.validTo)}까지` : '유효') : 'http://'}
        />
        <Check ok label="서버 신원·인스턴스 ID 확인" code={shortInstanceId(verification.instanceId)} />
        <Check
          ok={compatible}
          label={compatible ? '버전 호환' : '버전이 호환되지 않아요'}
          code={compatible ? `서버 ${verification.serverVersion}${appVersion ? ` · 앱 ${appVersion}` : ''}` : `지원 앱 ${verification.supportedClients.min} 이상${appVersion ? ` · 이 앱 ${appVersion}` : ''}`}
        />
      </div>
      {!tls.secure ? (
        <Notice tone="warn" icon="alert">
          개발용 localhost 연결이에요. 실제 회사 서버에는 HTTPS 주소로만 연결돼요.
        </Notice>
      ) : null}
      {verification.alreadyRegistered ? (
        <Notice tone="ok" icon="checkc">
          이미 이 앱에 등록된 회사예요. 로그인만 하면 돼요.
        </Notice>
      ) : null}
      {compatible ? (
        <Notice tone="neutral" icon="info">
          대화·파일은 이 회사 서버에 저장돼요. 회사 이름이나 주소가 다르면 연결하지 마세요.
        </Notice>
      ) : (
        <Notice tone="danger" icon="shield">
          이 앱 버전으로는 이 서버에 연결할 수 없어요. 앱을 업데이트하거나 회사 관리자에게 서버 업데이트를 요청하세요.
        </Notice>
      )}
    </div>
  );
}
